// Computed1<A, R>/Computed2<A, B, R>: a read-only reactive value derived
// from one or more state<T> sources, recomputed and re-notified whenever
// any source changes — React's useMemo/Angular signals' computed()
// equivalent. No new reactive primitive: built entirely on state<T>'s
// existing .Value/.Subscribe (including the unsubscribe handle Subscribe
// now returns — see kopscript's own LLM.md), the same way FormField<T>
// (forms.ks) is built on state<T> rather than introducing a second
// reactive system.
//
// Deliberately EXPLICIT dependencies, not automatic tracking (the way
// Angular signals' computed() infers its own deps by watching which
// signals get read during evaluation) — that needs a live "currently
// evaluating" context the compiler/runtime would have to thread through
// every read, a different kind of feature than anything else here.
// Naming each source explicitly is one honest step short of automatic,
// consistent with this project's own "explicit constructor args, no
// hidden magic" position elsewhere (dependency injection, routing).
//
// `Value` is `state<R>`, not a bare `R` — KopScript's declared-property
// grammar has no way to write a custom getter body (only auto-implemented
// `{ get; }`/`{ get; set; }`, backed by ordinary assignment), so a
// Computed can't expose a property that recomputes itself on read. state<R>
// already IS the "read the current value, or subscribe to it changing"
// shape this needs — Value.Value / Value.Subscribe(...), the exact same
// two members any other state<T> exposes, not a parallel API to learn.
//
// Fixed arity (1 and 2 sources) for the same reason CombineValidators2/3
// (forms.ks) are fixed-arity, not a general array-taking form: KopScript
// has no array-of-function-values type, and no way to express "N sources
// of N different types" without one type parameter per source. Add
// Computed3<A, B, C, R> the same way if a real derivation ever needs three.
class Computed1<A, R> {
  public state<R> Value { get; }

  constructor(state<A> source, (A) => R compute) {
    this.Value = state(compute(source.Value));
    source.Subscribe((A v) => { this.Value.Value = compute(v); });
  }
}

class Computed2<A, B, R> {
  public state<R> Value { get; }

  constructor(state<A> a, state<B> b, (A, B) => R compute) {
    this.Value = state(compute(a.Value, b.Value));
    a.Subscribe((A v) => { this.Value.Value = compute(v, b.Value); });
    b.Subscribe((B v) => { this.Value.Value = compute(a.Value, v); });
  }
}
